// Firebase Cloud Messaging — token registration and foreground message handling.
// See https://firebase.google.com/docs/cloud-messaging/js/client

import axios from 'axios';
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { API_LINK } from '../constants';

const firebaseConfig = {
  apiKey: 'AIzaSyCq5l57tKjFJkpx6mlBUxJCdO_aUAI0eEU',
  authDomain: 'cpsturnitin.firebaseapp.com',
  projectId: 'cpsturnitin',
  storageBucket: 'cpsturnitin.appspot.com',
  messagingSenderId: '994987528038',
  appId: '1:994987528038:web:3b5d7f3a867581f2ccc2a7',
};

const VAPID_KEY = 'BDuvCWTiuzHjg7gJR_l5bITeVSNTmw4mQ-wJ_Ysx18OfFlrRs0sLpih_9yHWgffGetbO6MpEN_Lkw3BP-_JZFfA';

if (!getApps().length) {
  initializeApp(firebaseConfig);
}

const messaging = getMessaging();

const swPath = `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`;

function logFcm(...args) {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[FCM]', ...args);
  }
}

async function unregisterConflictingServiceWorkers() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => {
    const scriptUrl = (
      registration.active
      || registration.waiting
      || registration.installing
    )?.scriptURL || '';

    const isFirebaseMessagingSw = scriptUrl.includes('firebase-messaging-sw.js');
    const isCraPwaSw = scriptUrl.includes('service-worker.js') && !isFirebaseMessagingSw;

    if (isCraPwaSw) {
      logFcm('unregistering conflicting PWA service worker:', scriptUrl);
      return registration.unregister();
    }

    return Promise.resolve(false);
  }));
}

async function findFirebaseMessagingRegistration() {
  const registrations = await navigator.serviceWorker.getRegistrations();
  return registrations.find((registration) => {
    const scriptUrl = (
      registration.active
      || registration.waiting
      || registration.installing
    )?.scriptURL || '';

    return scriptUrl.includes('firebase-messaging-sw.js');
  });
}

async function waitForActiveServiceWorker(registration) {
  if (registration.active) {
    return registration;
  }

  const worker = registration.installing || registration.waiting;
  if (worker) {
    if (worker.state === 'activated') {
      return registration;
    }

    await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Firebase messaging service worker activation timed out'));
      }, 30000);

      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') {
          clearTimeout(timeoutId);
          resolve();
        } else if (worker.state === 'redundant') {
          clearTimeout(timeoutId);
          reject(new Error('Firebase messaging service worker became redundant'));
        }
      });
    });

    return registration;
  }

  return navigator.serviceWorker.ready;
}

async function getMessagingServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported in this browser');
  }

  await unregisterConflictingServiceWorkers();

  let registration = await findFirebaseMessagingRegistration();
  if (!registration) {
    registration = await navigator.serviceWorker.register(swPath);
  }

  return waitForActiveServiceWorker(registration);
}

export const requestForToken = async (userId) => {
  try {
    const permission = await Notification.requestPermission();
    logFcm('notification permission:', permission);

    if (permission !== 'granted') {
      return null;
    }

    const registration = await getMessagingServiceWorkerRegistration();
    logFcm('service worker registered at', swPath, registration);

    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!currentToken) {
      logFcm('getToken returned empty — check VAPID key and service worker');
      return null;
    }

    logFcm('device token acquired:', `${currentToken.slice(0, 24)}...`);

    const response = await axios({
      method: 'post',
      url: `${API_LINK}api/createFCMToken`,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      data: {
        token: currentToken,
        user_id: userId,
      },
    });

    logFcm('createFCMToken response:', response.status, response.data);
    return currentToken;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[FCM] registration failed:', err);
    return null;
  }
};

export const onMessageListener = (callback) => onMessage(messaging, (payload) => {
  logFcm('foreground message:', payload && payload.notification);
  callback(payload);
});

export const ensureFirebaseMessagingServiceWorker = () => getMessagingServiceWorkerRegistration();
